<template>
  <nav
    :class="[`${theme}`, { closed: isClosed }]"
    v-if="this.selectedUser || this.authUser"
  >
    <div class="nav-header text-light">
      <div :class="['mb-3', { 'text-center': isClosed }]">
        <b-icon
          icon="list"
          :class="['t-icon menu-icon', { 'mr-3': !isClosed }]"
          data-test-id="navbar-collapse"
          @click="toggleNavigation"
        ></b-icon>
        <router-link
          class="mb-3 branding"
          :to="routeToHome"
          data-test-id="navbar-home"
        >
          <img class="navbar-logo" :src="uiLogo" />
        </router-link>
      </div>
      <slot name="header" />
    </div>
    <div class="t-navbar">
      <div
        class="t-navbar-item"
        v-for="(navItem, idx) in navItems"
        :key="idx"
        v-b-tooltip.hover.top
        :title="navItem.tooltip"
      >
        <router-link
          v-if="!navItem.disabled"
          :data-test-id="navItem.testID"
          :class="[
            't-navbar-item-link',
            { 't-navbar-item-link-with-subnav': navItem.subnav },
            { 'text-center': isClosed },
          ]"
          :to="navItem.link"
          :exact="navItem.exact"
        >
          <b-icon :icon="navItem.icon" :class="{ 'mr-3': !isClosed }"></b-icon>
          <span class="link-text">{{ navItem.name }}</span>
        </router-link>
        <div class="t-navbar-item-link" v-if="navItem.disabled">
          <b-icon
            :icon="navItem.icon"
            :class="{ 'mr-3': !isClosed }"
            style="color: #9fb9d0"
          ></b-icon>
          <span class="link-text" style="color: #9fb9d0">{{
            navItem.name
          }}</span>
        </div>
        <div class="subnav" v-if="navItem.subnav">
          <router-link
            :data-test-id="navItem.testID"
            v-if="isClosed && !navItem.disabled"
            class="subnav-link subnav-title"
            :to="navItem.link"
          >
            <span class="link-text">{{ navItem.name }}</span>
          </router-link>
          <div class="subnav-link subnav-title" v-if="navItem.disabled">
            <span class="link-text" style="color: #9fb9d0">{{
              navItem.name
            }}</span>
          </div>
          <div
            class="subnav-item"
            v-for="(subnavItem, idx) in navItem.subnav"
            :key="idx"
            v-b-tooltip.hover.top
            :title="subnavItem.tooltip"
          >
            <router-link
              v-if="!subnavItem.disabled"
              class="subnav-link"
              :to="subnavItem.link"
              :data-test-id="subnavItem.testID"
            >
              <span class="link-text">{{ subnavItem.name }}</span>
            </router-link>
            <div class="subnav-link subnav-title" v-if="subnavItem.disabled">
              <span class="link-text" style="color: #9fb9d0">{{
                subnavItem.name
              }}</span>
            </div>
          </div>
        </div>
        <div class="subnav" v-else>
          <router-link
            v-if="isClosed"
            class="subnav-link subnav-title"
            :to="navItem.link"
            :data-test-id="navItem.testID"
          >
            <span class="link-text">{{ navItem.name }}</span>
          </router-link>
        </div>
      </div>
    </div>
  </nav>
</template>

<script>
import { mapActions, mapGetters } from "vuex";

export default {
  name: "TNavbar",
  props: {
    closed: {
      type: Boolean,
      required: true,
    },
    navItems: {
      type: Array,
      required: true,
    },
    theme: {
      type: String,
      default: "msp",
    },
  },
  model: {
    prop: "closed",
    event: "change",
  },
  data() {
    return {
      breakpointXL: null,
      isClosed: this.closed,
    };
  },
  computed: {
    ...mapGetters("authentication", ["selectedUser", ["authUser"]]),
    ...mapGetters("userInterface", ["uiLogo"]),

    routeToHome() {
      const userRole = this.$userPrefs.getUserRole();
      switch (userRole) {
        case 1:
          return "/system/overview";
        case 2:
          return "/sales/msps";
        case 3:
          return "/msp/overview";
        case 4:
          return "/customer/overview";
        default:
          return "/";
      }
    },
  },
  methods: {
    ...mapActions("users", ["fetchCustomerAccounts", "fetchCustomerAccount"]),
    ...mapActions("authentication", ["setUser"]),
    toggleNavigation() {
      this.isClosed = !this.isClosed;
      this.$emit("change", this.isClosed);
    },
    onWindowResize(matchMedia) {
      /* istanbul ignore else */
      if (matchMedia.matches && this.isClosed !== true) {
        this.isClosed = true;
        this.$emit("change", this.isClosed);
      }

      /* istanbul ignore else */
      if (!matchMedia.matches && this.isClosed !== false) {
        this.isClosed = false;
        this.$emit("change", this.isClosed);
      }
    },
  },
  mounted() {
    const appStyle = window.getComputedStyle(document.body);
    this.breakpointXL = parseInt(
      appStyle.getPropertyValue("--breakpoint-xl").replace("px", "")
    );

    const matchXL = window.matchMedia("(max-width: 1200px)");

    matchXL.addEventListener("change", this.onWindowResize);
    this.onWindowResize(matchXL);
  },
};
</script>

<style lang="scss" scoped>
@mixin active-link {
  color: $light;
  text-decoration: none;
  background-color: $customer-secondaryNav;
}

@mixin active-subnav-link {
  color: $light;
  font-weight: 700;
  text-decoration: none;
  box-shadow: inset 0.1875rem 0 0 0 $customer-secondaryNavAccent;
}

nav {
  width: 250px;
  transition: width 0.3s ease-in-out;
  background-color: $primary;

  &.msp {
    background-color: $msp-primary;

    .nav-header {
      border-bottom: 1px solid $msp-primaryLight;
    }

    .t-navbar {
      &-item {
        &-link {
          &.router-link-active {
            background-color: $msp-secondaryNav;
            box-shadow: inset 0.1875rem 0 0 0 $msp-secondaryNavAccent;
          }

          &.router-link-active + .subnav {
            background-color: $msp-secondaryNav;
          }

          &:hover {
            @include active-subnav-link;
            background-color: $msp-secondaryNavAccent;
          }
        }

        .subnav {
          box-shadow: inset 0.1875rem 0 0 0 $msp-subnavNavAccent;

          &-link {
            background-color: $msp-secondaryNav;
            box-shadow: inset 0.1875rem 0 0 0 $msp-subnavNavAccent;

            &.router-link-active {
              @include active-subnav-link;
              background-color: $msp-subnavNavAccentActive;
            }

            &:hover {
              color: $light;
              text-decoration: none;
            }
          }
        }
      }
    }

    &.closed {
      .t-navbar {
        &-item {
          &-link {
            &.router-link-active {
              background-color: $msp-secondaryNav;
              color: $white;
            }
          }

          .subnav {
            .subnav-title {
              background-color: $msp-secondaryNavAccent;
            }
          }
        }
      }
    }
  }

  &.sysadmin {
    background-color: $sysadmin-primary;

    .nav-header {
      border-bottom: 1px solid $sysadmin-primaryLight;
    }

    .t-navbar {
      &-item {
        &-link {
          &.router-link-active {
            background-color: $sysadmin-secondaryNav;
            box-shadow: inset 0.1875rem 0 0 0 $sysadmin-secondaryNavAccent;
          }

          &.router-link-active + .subnav {
            background-color: $sysadmin-secondaryNav;
          }

          &:hover {
            @include active-subnav-link;
            background-color: $sysadmin-secondaryNavAccent;
          }
        }

        .subnav {
          box-shadow: inset 0.1875rem 0 0 0 $sysadmin-subnavNavAccent;

          &-link {
            background-color: $sysadmin-secondaryNav;
            box-shadow: inset 0.1875rem 0 0 0 $sysadmin-subnavNavAccent;

            &.router-link-active {
              @include active-subnav-link;
              background-color: $sysadmin-subnavNavAccentActive;
            }

            &:hover {
              color: $light;
              text-decoration: none;
            }
          }
        }
      }
    }

    &.closed {
      .t-navbar {
        &-item {
          &-link {
            &.router-link-active {
              background-color: $sysadmin-secondaryNav;
              color: $white;
            }
          }

          .subnav {
            .subnav-title {
              background-color: $sysadmin-secondaryNavAccent;
            }
          }
        }
      }
    }
  }

  .nav-header {
    margin: 1.25rem 1rem 2rem;
    padding: 0 0 2rem;
    border-bottom: 1px solid #669efc;

    .menu-icon {
      font-size: 1.5rem;

      &:hover {
        cursor: pointer;
      }
    }
  }

  .t-navbar {
    &-item {
      position: relative;
      line-height: 1rem;

      &-link {
        display: flex;
        align-items: center;
        padding: 1.25rem 1rem;
        color: $light;

        &.router-link-active,
        &:hover {
          @include active-link;
        }

        &.router-link-active {
          box-shadow: inset 0.1875rem 0 0 0 $customer-secondaryNavAccent;
        }

        &-with-subnav.router-link-active {
          box-shadow: inset 0.1875rem 0 0 0 $customer-subnavNavAccent;

          .bi-chevron-down {
            transform: rotate(180deg);
          }
        }

        .link-text {
          flex: 1;
          white-space: nowrap;
        }
      }

      .subnav {
        display: none;
        box-shadow: inset 0.1875rem 0 0 0 $customer-secondaryNavAccent;

        &-link {
          display: flex;
          padding: 1rem 1rem 1rem 3rem;
          color: $light;
          box-shadow: inset 0.1875rem 0 0 0 $customer-subnavNavAccent;
          background-color: $customer-secondaryNav;

          &.router-link-active {
            @include active-subnav-link;
            background-color: $customer-subnavNavAccentActive;
          }

          &:hover {
            color: $light;
            text-decoration: none;
            box-shadow: inset 0.1875rem 0 0 0 $customer-secondaryNavAccent;
          }
        }
      }
    }
  }

  &:not(.closed) {
    overflow-y: overlay;

    .t-navbar {
      &-item {
        &-link {
          &.router-link-active + .subnav {
            display: block;
          }
        }
      }
    }
  }

  &.closed {
    width: 3.5rem;

    .nav-header {
      margin: 1.25rem 0 2rem;

      .branding {
        display: none;
      }
    }

    .t-navbar {
      &-item {
        &:hover {
          .subnav {
            display: block;
          }
        }

        &-link {
          padding: 1.25rem;

          .link-text {
            display: none;
          }

          &:hover,
          &:focus,
          &.router-link-active {
            box-shadow: none;
            background: white;
            color: $customer-secondaryNav;
          }
        }

        .subnav {
          min-width: 200px;
          position: absolute;
          top: 0;
          left: 100%;
          z-index: 2;
          background-color: #0e153a;
          border-top-right-radius: 4px;
          border-bottom-right-radius: 4px;
          overflow: hidden;

          &-link {
            padding: 1.25rem 1rem;
            font-weight: normal;
          }

          .subnav-title {
            color: white;
            font-weight: bold;
            background-color: $customer-primary;
            padding: 1.25rem 1rem;
            box-shadow: none;
          }
        }
      }
    }
  }
}

@media only screen and (max-width: 1200px) {
  nav {
    position: absolute;
    height: 100vh;
    z-index: 2;
  }
}
</style>
